import { IAppContext } from 'App/AppContext';
import { AICriterionOutcomeSourceKind } from 'models/auditCriteriaGeneric';
import { createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { processStartTemplate } from 'models/copilot/actions/aiActionTemplates';
import { TenantProcess } from 'models/copilot/tenantProcessStateBase';
import Entity from 'models/entity';
import { getEntityUrl } from 'utils/url';

// The processes whose outcomes "Open the result" opens in the panel: the two Supplier investigations (PLAN-073) and the
// AI evidence audit (PLAN-077), which the server starts in the background and the panel opens on the outcome's entity
const openableProcesses: number[] = [
  TenantProcess.SupplierIncidents,
  TenantProcess.SupplierCompliance,
  TenantProcess.EntityEvidenceAudit,
];

/**
 * The process of an outcome a process run proposed ("<process>:<run>"), when the web can open that run; undefined for
 * an agent turn, a check or a process the web does not run. The evidence audit's run is
 * "{entityType}/{entityId}/{time}": it is keyed on the entity, which is the outcome's own item.
 */
export const getOutcomeRunProcess = (
  sourceKind: AICriterionOutcomeSourceKind,
  sourceRef: string,
): TenantProcess | undefined => {
  if (sourceKind !== AICriterionOutcomeSourceKind.ProcessRun) return undefined;

  const name = sourceRef.split(':')[0];
  const process = /^\d+$/.test(name) ? Number(name) : TenantProcess[name as keyof typeof TenantProcess];

  return typeof process === 'number' && openableProcesses.includes(process) ? (process as TenantProcess) : undefined;
};

/**
 * "Open the result" of a process run's outcome (spec 3.5): the item's dossier, and the user's own run of the process for
 * the item in the panel, running or finished, never started again. Without a run of this user (another person's, or a
 * failed one) the process starts.
 */
export const openAssessmentAutomationRun = (
  appContext: IAppContext,
  process: TenantProcess,
  entityType: number,
  entityId: number,
): void => {
  appContext.copilotBridge.requestNavigation(getEntityUrl(new Entity(entityId, entityType)));
  appContext.copilotBridge.requestAction({
    binding: createAIActionBinding(processStartTemplate, { processId: process }),
    processStartInput: { entityType: entityType, entityId: entityId },
    open: true,
  });
};
